/** Structural, so a row read from any query shows the same way as long as it carries the fields. */
export interface GrammarTableCell {
  kind: string;
  text?: string | null;
}

export interface GrammarTable {
  heading?: string | null;
  rows: GrammarTableCell[][];
}

export interface GrammarPoint {
  position: number;
  text: string;
  description?: string | null;
  grammarNote?: string | null;
  note?: string | null;
  tables?: GrammarTable[] | null;
}

/**
 * The substitution tables the page prints, kept as a grid. The pairing is the teaching — a flat
 * list of cells would licence combinations the book does not teach. A merge marker draws empty.
 */
function GrammarTables({ tables }: { tables: GrammarTable[] }) {
  return (
    <div className="space-y-3 overflow-x-auto">
      {tables.map((table, tableIndex) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: positional table content
          key={`table-${tableIndex}`}
        >
          {table.heading && (
            <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
              {table.heading}
            </p>
          )}
          <table className="text-xs border-collapse">
            <tbody>
              {table.rows.map((cells, rowIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional row content
                <tr key={`row-${rowIndex}`}>
                  {cells.map((cell, cellIndex) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: positional cell content
                      key={`cell-${rowIndex}-${cellIndex}`}
                      className="border border-gray-200 dark:border-gray-800 px-2 py-1 whitespace-nowrap"
                    >
                      {cell.text ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/** A grammar point as the book prints it: everything it carries, the generator's reading included. */
export function GrammarPointList({ points }: { points: GrammarPoint[] }) {
  return (
    <div className="space-y-3">
      {points.map((point) => (
        <article
          key={point.position}
          className="border-l-2 border-accent-500/60 pl-3.5 space-y-2 text-sm"
        >
          <h4 className="text-sm font-bold">{point.text}</h4>
          {point.description && <p>{point.description}</p>}
          {point.grammarNote && (
            <p className="text-gray-600 dark:text-gray-400">{point.grammarNote}</p>
          )}
          {point.note && <p className="text-xs text-gray-500">{point.note}</p>}
          {point.tables && point.tables.length > 0 && <GrammarTables tables={point.tables} />}
        </article>
      ))}
    </div>
  );
}
